import React, { useEffect, useState } from "react";
import { API_BASE } from "../api/config";
import LocationSelect from "../components/LocationSelect";
import "./BossOverviewPanel.css";

const SHOPS = [
  "ALL",
  "GMP",
  "OSHT",
  "OSPF",
  "MT",
  "JS",
  "JCHT",
  "JCPF",
  "MDYHT",
  "MDYPF",
];

function todayKey() {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 10);
}

function formatMoney(value) {
  return Number(value || 0).toLocaleString();
}

function formatPercent(value) {
  const n = Number(value || 0);
  return `${n >= 0 ? "+" : ""}${n.toFixed(2)}%`;
}

export default function BossOverviewPanel() {
  const [date, setDate] = useState(todayKey());
  const [shop, setShop] = useState("ALL");

  const [salesSummary, setSalesSummary] = useState({});
  const [topShops, setTopShops] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [alertSummary, setAlertSummary] = useState({ total: 0, high: 0, medium: 0 });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  async function loadOverview() {
    try {
      setLoading(true);
      setError("");

      const [salesRes, alertsRes] = await Promise.all([
        fetch(`${API_BASE}/api/erp/sales-analytics?shop=${shop}&date=${date}`),
        fetch(`${API_BASE}/api/erp/finance-alerts?shop=${shop}&date=${date}`),
      ]);

      const salesData = await salesRes.json();
      const alertsData = await alertsRes.json();

      if (!salesRes.ok || salesData.success === false) {
        throw new Error(salesData.message || "Failed to load sales overview");
      }

      if (!alertsRes.ok || alertsData.success === false) {
        throw new Error(alertsData.message || "Failed to load alerts overview");
      }

      setSalesSummary(salesData.summary || {});
      setTopShops((salesData.topShops || []).slice(0, 5));
      setAlerts((alertsData.rows || []).slice(0, 5));
      setAlertSummary(alertsData.summary || { total: 0, high: 0, medium: 0 });
    } catch (err) {
      setError(err.message || "Boss overview loading failed");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadOverview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, shop]);

  const status = salesSummary.status || "SAME";
  const isDown = status === "DOWN";

  return (
    <div className="bossOverview">
      <div className="bossOverview__head">
        <div>
          <span>Overview</span>
          <h2>Boss Quick Summary</h2>
          <p>Clean management view for sales, top shops and important alerts.</p>
        </div>

        <div className="bossOverview__filters">
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />

          <LocationSelect value={shop} onChange={setShop} mode="shop" includeAll />

          <button onClick={loadOverview} disabled={loading}>
            {loading ? "Loading..." : "Refresh"}
          </button>
        </div>
      </div>

      {error && <div className="bossOverview__error">{error}</div>}

      <div className="bossOverview__cards">
        <div className="bossOverviewCard bossOverviewCard--sales">
          <span>Today Sales</span>
          <strong>{formatMoney(salesSummary.todaySales)} Ks</strong>
          <small>{formatMoney(salesSummary.todayVouchers)} vouchers</small>
        </div>

        <div className={isDown ? "bossOverviewCard bossOverviewCard--danger" : "bossOverviewCard bossOverviewCard--good"}>
          <span>Sales Status</span>
          <strong>{status}</strong>
          <small>{formatPercent(salesSummary.salesChangePercent)} from yesterday</small>
        </div>

        <div className="bossOverviewCard">
          <span>Qty Sold</span>
          <strong>{formatMoney(salesSummary.todayQty)}</strong>
          <small>total item qty</small>
        </div>

        <div className="bossOverviewCard">
          <span>Average Bill</span>
          <strong>{formatMoney(salesSummary.todayAverageBill)} Ks</strong>
          <small>sales per voucher</small>
        </div>

        
            <div
              className="bossStatCard bossMemberQuickCard"
              role="button"
              tabIndex={0}
              onClick={() => {
                window.location.href = "/erp/members";
              }}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  window.location.href = "/erp/members";
                }
              }}
            >
              <span>Members</span>
              <strong>Open</strong>
              <small>Member list, QR, Excel import & history</small>
            </div>
<div className={alertSummary.total > 0 ? "bossOverviewCard bossOverviewCard--danger" : "bossOverviewCard bossOverviewCard--good"}>
          <span>Alerts</span>
          <strong>{formatMoney(alertSummary.total)}</strong>
          <small>{formatMoney(alertSummary.high)} high priority</small>
        </div>
      </div>

      <div className="bossOverview__grid">
        <div className="bossOverviewBox">
          <div className="bossOverviewBox__title">
            <strong>Top Shops Today</strong>
            <small>Top 5 selling shops</small>
          </div>

          {topShops.length === 0 ? (
            <div className="bossOverviewEmpty">No shop sales data.</div>
          ) : (
            <div className="bossOverviewList">
              {topShops.map((row, index) => (
                <div key={row.shopCode} className="bossOverviewList__row">
                  <b>#{index + 1} {row.shopCode}</b>
                  <span>{formatMoney(row.sales)} Ks</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bossOverviewBox">
          <div className="bossOverviewBox__title">
            <strong>Important Alerts</strong>
            <small>Top 5 warning items</small>
          </div>

          {alerts.length === 0 ? (
            <div className="bossOverviewGood">✅ No warning alerts</div>
          ) : (
            <div className="bossOverviewAlerts">
              {alerts.map((alert, index) => (
                <div key={`${alert.type}-${alert.shopCode}-${index}`} className="bossOverviewAlert">
                  <div>
                    <b>{alert.title}</b>
                    <p>{alert.message}</p>
                  </div>
                  <span>{alert.severity}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


